import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, ArrowRight, CheckCircle2, BookOpen, Clock
} from 'lucide-react'
import { supabase } from '../lib/supabase'

interface Lesson {
  id: string
  title: string
  content: string
  order_index: number
  duration_minutes: number
  video_url: string | null
  module_id: string
}

interface Module {
  id: string
  title: string
  course_id: string
}

interface LessonPageProps {
  user: any
}

export default function LessonPage({ user }: LessonPageProps) {
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>()
  const navigate = useNavigate()
  const [lesson, setLesson] = useState<Lesson | null>(null)
  const [module, setModule] = useState<Module | null>(null)
  const [loading, setLoading] = useState(true)
  const [nextLesson, setNextLesson] = useState<Lesson | null>(null)
  const [previousLesson, setPreviousLesson] = useState<Lesson | null>(null)
  const [completed, setCompleted] = useState(false)

  useEffect(() => {
    if (lessonId && courseId) {
      fetchLessonData()
    }
  }, [lessonId, courseId])

  const fetchLessonData = async () => {
    try {
      // Fetch lesson
      const { data: lessonData } = await supabase
        .from('course_lessons')
        .select('*')
        .eq('id', lessonId)
        .single()

      if (lessonData) {
        setLesson(lessonData)

        // Fetch module
        const { data: moduleData } = await supabase
          .from('course_modules')
          .select('*')
          .eq('id', lessonData.module_id)
          .single()

        if (moduleData) {
          setModule(moduleData)
          
          // Fetch all lessons in this module to find next/previous
          const { data: allLessons } = await supabase
            .from('course_lessons')
            .select('*')
            .eq('module_id', lessonData.module_id)
            .order('order_index', { ascending: true })

          if (allLessons) {
            const currentIndex = allLessons.findIndex(l => l.id === lessonId)
            if (currentIndex > 0) {
              setPreviousLesson(allLessons[currentIndex - 1])
            }
            if (currentIndex < allLessons.length - 1) {
              setNextLesson(allLessons[currentIndex + 1])
            } else {
              // If this is the last lesson of the module, try to find first lesson of next module
              await findNextModuleFirstLesson(moduleData.course_id, moduleData.id)
            }
          }
        }

        // Check if lesson is completed
        if (user) {
          await checkLessonCompletion()
        }
      }
    } catch (error) {
      console.error('Error fetching lesson:', error)
    } finally {
      setLoading(false)
    }
  }

  const findNextModuleFirstLesson = async (courseId: string, currentModuleId: string) => {
    // Get current module order
    const { data: currentModule } = await supabase
      .from('course_modules')
      .select('order_index')
      .eq('id', currentModuleId)
      .single()

    if (!currentModule) return

    // Find next module
    const { data: nextModule } = await supabase
      .from('course_modules')
      .select('*')
      .eq('course_id', courseId)
      .gt('order_index', currentModule.order_index)
      .order('order_index', { ascending: true })
      .limit(1)
      .single()

    if (nextModule) {
      // Get first lesson of next module
      const { data: firstLesson } = await supabase
        .from('course_lessons')
        .select('*')
        .eq('module_id', nextModule.id)
        .order('order_index', { ascending: true })
        .limit(1)
        .single()

      if (firstLesson) {
        setNextLesson(firstLesson)
      }
    }
  }

  const checkLessonCompletion = async () => {
    if (!user || !lessonId) return

    const { data } = await supabase
      .from('user_course_progress')
      .select('completed_lessons')
      .eq('user_id', user.id)
      .eq('course_id', courseId)
      .single()

    if (data && data.completed_lessons) {
      const lessons = Array.isArray(data.completed_lessons) 
        ? data.completed_lessons 
        : []
      setCompleted(lessons.includes(lessonId))
    }
  }

  const markAsCompleted = async () => {
    if (!user || !lessonId || completed) return

    try {
      // Get current progress and total lessons count
      const { data: progress } = await supabase
        .from('user_course_progress')
        .select('completed_lessons')
        .eq('user_id', user.id)
        .eq('course_id', courseId)
        .single()

      if (progress) {
        let completedLessons = progress.completed_lessons || []
        // Handle JSONB format
        completedLessons = Array.isArray(completedLessons) 
          ? completedLessons 
          : []
        
        if (!completedLessons.includes(lessonId)) {
          completedLessons.push(lessonId)

          // Get total lessons count for this course
          // First get all module IDs for this course
          const { data: courseModules } = await supabase
            .from('course_modules')
            .select('id')
            .eq('course_id', courseId)
          
          const moduleIds = courseModules?.map(m => m.id) || []
          
          // Then count lessons in those modules
          const { count: totalLessons } = await supabase
            .from('course_lessons')
            .select('id', { count: 'exact', head: true })
            .in('module_id', moduleIds)

          // Calculate progress percentage
          const progressPercentage = totalLessons 
            ? Math.round((completedLessons.length / totalLessons) * 100)
            : 0

          // Update progress
          const { error } = await supabase
            .from('user_course_progress')
            .update({ 
              completed_lessons: completedLessons,
              current_lesson_id: nextLesson?.id || lessonId,
              last_accessed_at: new Date().toISOString(),
              progress_percentage: progressPercentage,
              status: progressPercentage >= 100 ? 'completed' : 'in_progress',
              completed_at: progressPercentage >= 100 ? new Date().toISOString() : null
            })
            .eq('user_id', user.id)
            .eq('course_id', courseId)

          if (!error) {
            setCompleted(true)
            console.log('Lección marcada como completada. Progreso:', progressPercentage + '%')
          } else {
            console.error('Error updating progress:', error)
            alert('Error al marcar la lección como completada: ' + error.message)
          }
        }
      }
    } catch (error) {
      console.error('Error marking lesson as completed:', error)
      alert('Error al completar la lección')
    }
  }

  const handleNextLesson = () => {
    if (nextLesson) {
      navigate(`/cursos/${courseId}/leccion/${nextLesson.id}`)
    }
  }

  const handlePreviousLesson = () => {
    if (previousLesson) {
      navigate(`/cursos/${courseId}/leccion/${previousLesson.id}`)
    }
  }

  // Simple markdown-like content renderer
  const renderContent = (content: string) => {
    if (!content) return null

    const lines = content.split('\n')
    const elements: JSX.Element[] = []
    let key = 0

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim()
      
      if (!line) {
        elements.push(<div key={key++} className="h-4" />)
        continue
      }

      // Headers
      if (line.startsWith('### ')) {
        elements.push(
          <h3 key={key++} className="text-xl font-semibold text-neutral-700 mb-2 mt-4">
            {line.replace('### ', '')}
          </h3>
        )
      } else if (line.startsWith('## ')) {
        elements.push(
          <h2 key={key++} className="text-2xl font-bold text-neutral-800 mb-3 mt-6">
            {line.replace('## ', '')}
          </h2>
        )
      } else if (line.startsWith('# ')) {
        elements.push(
          <h1 key={key++} className="text-3xl font-bold text-neutral-800 mb-4 mt-8 first:mt-0">
            {line.replace('# ', '')}
          </h1>
        )
      }
      // Lists
      else if (line.startsWith('- ') || line.startsWith('* ')) {
        const items = []
        let j = i
        while (j < lines.length && (lines[j].trim().startsWith('- ') || lines[j].trim().startsWith('* '))) {
          items.push(lines[j].trim().replace(/^[-*] /, ''))
          j++
        }
        elements.push(
          <ul key={key++} className="list-disc list-inside mb-4 space-y-2">
            {items.map((item, idx) => (
              <li key={idx} className="text-neutral-700">{item}</li>
            ))}
          </ul>
        )
        i = j - 1
      }
      // Numbered lists
      else if (/^\d+\. /.test(line)) {
        const items = []
        let j = i
        while (j < lines.length && /^\d+\. /.test(lines[j].trim())) {
          items.push(lines[j].trim().replace(/^\d+\. /, ''))
          j++
        }
        elements.push(
          <ol key={key++} className="list-decimal list-inside mb-4 space-y-2">
            {items.map((item, idx) => (
              <li key={idx} className="text-neutral-700">{item}</li>
            ))}
          </ol>
        )
        i = j - 1
      }
      // Blockquote
      else if (line.startsWith('> ')) {
        elements.push(
          <blockquote key={key++} className="border-l-4 border-education pl-4 italic text-neutral-600 my-4">
            {line.replace('> ', '')}
          </blockquote>
        )
      }
      // Regular paragraph
      else {
        // Process inline formatting (bold, links)
        const processInlineFormatting = (text: string) => {
          // Bold with ** or __
          text = text.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-neutral-800">$1</strong>')
          text = text.replace(/__(.+?)__/g, '<strong class="font-semibold text-neutral-800">$1</strong>')
          // Links
          text = text.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-education hover:underline">$1</a>')
          return text
        }

        elements.push(
          <p 
            key={key++} 
            className="text-neutral-700 mb-4 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: processInlineFormatting(line) }}
          />
        )
      }
    }

    return elements
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-education"></div>
      </div>
    )
  }

  if (!lesson || !module) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-neutral-800 mb-2">Lección no encontrada</h2>
          <Link to={`/cursos/${courseId}`} className="text-education hover:underline">
            Volver al curso
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link 
              to={`/cursos/${courseId}`}
              className="flex items-center text-neutral-600 hover:text-education transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              <span className="hidden sm:inline">Volver al curso</span>
            </Link>

            <div className="flex items-center gap-4">
              <button
                onClick={markAsCompleted}
                disabled={completed}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  completed
                    ? 'bg-action text-white cursor-not-allowed'
                    : 'bg-education text-white hover:bg-education-dark'
                }`}
              >
                <CheckCircle2 className="w-5 h-5" />
                <span className="hidden sm:inline">
                  {completed ? 'Completada' : 'Marcar como completada'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Module and lesson info */}
          <div className="mb-6">
            <div className="flex items-center gap-2 text-sm text-neutral-600 mb-2">
              <BookOpen className="w-4 h-4" />
              <span>{module.title}</span>
            </div>
            <h1 className="font-heading font-bold text-3xl text-neutral-800 mb-3">
              {lesson.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-neutral-600">
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                <span>{lesson.duration_minutes} minutos</span>
              </div>
              {completed && (
                <div className="flex items-center text-action">
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  <span>Completada</span>
                </div>
              )}
            </div>
          </div>

          {/* Video (if exists) */}
          {lesson.video_url && (
            <div className="mb-8">
              <div className="aspect-video bg-neutral-900 rounded-lg overflow-hidden">
                <iframe
                  src={lesson.video_url}
                  className="w-full h-full"
                  allowFullScreen
                  title={lesson.title}
                />
              </div>
            </div>
          )}

          {/* Lesson content */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <div className="prose prose-lg max-w-none">
              {renderContent(lesson.content)}
            </div>
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center justify-between">
            <button
              onClick={handlePreviousLesson}
              disabled={!previousLesson}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-colors ${
                previousLesson
                  ? 'bg-white border border-neutral-300 text-neutral-700 hover:bg-neutral-50'
                  : 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
              }`}
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Anterior</span>
            </button>

            <button
              onClick={handleNextLesson}
              disabled={!nextLesson}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-colors ${
                nextLesson
                  ? 'bg-education text-white hover:bg-education-dark'
                  : 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
              }`}
            >
              <span>Siguiente</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
